import React from 'react';
import {
    Card,
    Form,
    Input,
    Button,
    Row,
    Col,
    Typography,
    Layout,
    Checkbox,
    message,
    Space
} from 'antd';
import {DownOutlined} from '@ant-design/icons';
import {reportService} from "../../services/domain/report/service";

const {Title} = Typography;
const {Content} = Layout;

/**
 * 會員名錄匯出頁面
 *
 * 允許用戶根據條件產生會員名錄報表
 */
const MemberDirectoryExport: React.FC = () => {
    const [form] = Form.useForm();

    const handleMemberIdStartValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        form.setFieldsValue({
            memberIdStart: value,
            memberIdEnd: value
        });
    };

    const handleMembershipLevelStartValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        form.setFieldsValue({
            membershipLevelStart: value,
            membershipLevelEnd: value
        });
    };

    // 處理預覽報表
    const handlePreviewReport = async () => {
        try {
            // 檢查表單是否有效
            await form.validateFields();

            const values = form.getFieldsValue();
            console.log('表單數據:', values);

            const {
                includeMemberMaster,
                includeProductMapping,
                memberIdStart,
                memberIdEnd,
                membershipLevelStart,
                membershipLevelEnd,
                delegateMaxCount,
                productMaxCount,
                certificationMaxCount,
                fieldDelimiter,
            } = values;

            // 整理符合 API 格式的請求資料
            const requestData = {
                // 起始會員編號
                memberIdStart: memberIdStart || '',
                // 結束會員編號
                memberIdEnd: memberIdEnd || '',
                // 起始會員級數
                membershipLevelStart: membershipLevelStart || '',
                // 結束會員級數
                membershipLevelEnd: membershipLevelEnd || '',
                // 會員代表最大數量
                delegateMaxCount: Number(delegateMaxCount) || 0,
                // 會員產品最大數量
                productMaxCount: Number(productMaxCount) || 0,
                // 會員認證最大數量
                certificationMaxCount: Number(certificationMaxCount) || 0,
                // 分隔符號
                fieldDelimiter: fieldDelimiter || '-+',
            };

            if (!includeMemberMaster && !includeProductMapping) {
                message.warning('轉檔內容請選擇至少一種類型');
                return;

            }
            // 根據勾選呼叫對應的匯出服務
            if (includeMemberMaster) {
                await reportService.exportMemberMainFile(requestData);
            }

            if (includeProductMapping) {
                await reportService.exportProductMainFile(requestData);
            }

        } catch (error) {
            console.error('表單驗證錯誤:', error);
            message.error('請檢查表單並重試');
        }
    };


    return (
        <Layout className="member-directory-export-page">
            <Content>
                {/* 頁面標題 */}
                <Row justify="center" className="mb-lg">
                    <Col>
                        <Title level={3} className="membership-title-with-emoji">
                            會員名錄匯出
                        </Title>
                    </Col>
                </Row>
                <Row justify="end" className="mb-lg">
                    <Col>
                        <Button
                            type="primary"
                            icon={<DownOutlined/>}
                            onClick={handlePreviewReport}
                        >
                            下載
                        </Button>
                    </Col>
                </Row>

                {/* 主要表單 */}
                <Card>
                    <Form
                        form={form}
                        layout="horizontal"
                        labelCol={{flex: '0 0 140px'}}
                        wrapperCol={{flex: '1 1 0'}}
                        initialValues={{
                            includeMemberMaster: true,
                            includeProductMapping: true,
                            delegateMaxCount: 5,
                            productMaxCount: 200,
                            certificationMaxCount: 20,
                            fieldDelimiter: '-+',
                        }}
                    >
                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item label="會員證號" style={{marginBottom: 16}}>
                                    <Input.Group compact>
                                        <Form.Item
                                            name="memberIdStart"
                                            noStyle
                                        >
                                            <Input style={{width: 120}} placeholder="起始值" onChange={handleMemberIdStartValueChange}/>
                                        </Form.Item>
                                        <Form.Item
                                            name="memberIdEnd"
                                            noStyle
                                        >
                                            <Input style={{width: 120}} placeholder="迄值" />
                                        </Form.Item>
                                    </Input.Group>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="會員級數" style={{marginBottom: 16}}>
                                    <Input.Group compact>
                                        <Form.Item
                                            name="membershipLevelStart"
                                            noStyle
                                        >
                                            <Input style={{width: 120}} placeholder="起始值" onChange={handleMembershipLevelStartValueChange}/>
                                        </Form.Item>
                                        <Form.Item
                                            name="membershipLevelEnd"
                                            noStyle
                                        >
                                            <Input style={{width: 120}} placeholder="迄值"/>
                                        </Form.Item>
                                    </Input.Group>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="會員代表最大數量"
                                    name="delegateMaxCount"
                                    style={{marginBottom: 16}}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="會員產品最大數量"
                                    name="productMaxCount"
                                    style={{marginBottom: 16}}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="會員認證最大數量"
                                    name="certificationMaxCount"
                                    style={{marginBottom: 16}}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="分隔符號"
                                    name="fieldDelimiter"
                                    style={{marginBottom: 16}}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="轉檔內容"
                                    style={{marginBottom: 16}}
                                >
                                    <Space.Compact>
                                        <Form.Item name="includeMemberMaster" valuePropName="checked" noStyle>
                                            <Checkbox>會員主檔</Checkbox>
                                        </Form.Item>
                                        <Form.Item name="includeProductMapping" valuePropName="checked" noStyle>
                                            <Checkbox>產品對照表</Checkbox>
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </Content>
        </Layout>
    );
};

export default MemberDirectoryExport;
